import React, { useState, useEffect, useRef } from 'react';
import OPDSlipPrint from './OPDSlipPrint';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  InputAdornment,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search,
  PersonAdd,
  Send,
  Cancel,
  Favorite,
  LocalHospital,
  Print
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { patientAPI, visitAPI } from '../services/api';

/* Pull the medical profile fields from any patient object shape */
const extractMedicalProfile = (p) => ({
  chiefComplaint:    p?.chiefComplaint    || p?.chief_complaint    || '',
  pastMedicalHistory:p?.pastMedicalHistory|| p?.past_medical_history|| '',
  currentMedications:p?.currentMedications|| p?.current_medications || '',
  allergies:         p?.allergies         || '',
  familyHistory:     '',
  lifestyleHabits:   ''
});


function OPDRegistration({ preselectedPatient, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [visitId, setVisitId] = useState(null);
  const [visitInfo, setVisitInfo] = useState(null);

  const [vitals, setVitals] = useState({
    bloodPressure: null,
    temperature: null,
    pulseRate: null,
    respiratoryRate: null,
    weight: null,
    height: null,
    bmi: null,
    oxygenSaturation: null,
    painScale: 0
  });

  const [medicalHistory, setMedicalHistory] = useState({
    chiefComplaint: '',
    pastMedicalHistory: '',
    currentMedications: '',
    allergies: '',
    familyHistory: '',
    lifestyleHabits: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [slipDialog, setSlipDialog] = useState(false);
  const [submittedVitals, setSubmittedVitals] = useState(null);
  const [autoBill, setAutoBill] = useState(null); // { billId, billNumber } from auto-gen
  const slipRef = useRef();

  useEffect(() => {
    const initializePatient = async () => {
      if (preselectedPatient) {
        console.log('Preselected patient:', preselectedPatient);
        // Convert snake_case to camelCase for consistency with internal patient data format
        const normalizedPatient = {
          id: preselectedPatient.id,
          patientCode: preselectedPatient.patient_code || preselectedPatient.patientCode,
          firstName: preselectedPatient.first_name || preselectedPatient.firstName,
          lastName: preselectedPatient.last_name || preselectedPatient.lastName,
          phone: preselectedPatient.phone,
          gender: preselectedPatient.gender,
          dateOfBirth: preselectedPatient.date_of_birth || preselectedPatient.dateOfBirth,
          email: preselectedPatient.email,
          address: preselectedPatient.address
        };

        // Inline the handleSelectPatient logic here to avoid dependency issues
        setSelectedPatient(normalizedPatient);
        setMedicalHistory(extractMedicalProfile(preselectedPatient));
        setError('');
        setSuccess('');
        setLoading(true);

        try {
          const response = await visitAPI.create({
            patientId: normalizedPatient.id,
            visitType: 'opd'
          });
          const data = response.data.data || response.data;
          setVisitId(data.visitId);
          setVisitInfo(data);
          setShowVitalsForm(true);
          if (data.billId) setAutoBill({ billId: data.billId, billNumber: data.billNumber });
          console.log('Visit created, showing vitals form');
        } catch (err) {
          console.error('Failed to create visit:', err);
          setError('Failed to create visit. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        loadAllPatients();
      }
    };

    initializePatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedPatient]);

  const loadAllPatients = async () => {
    setLoading(true);
    try {
      const response = await patientAPI.search('');
      const data = response.data.data || response.data;
      // Sort by ID descending (newest first)
      const sortedData = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
      setPatients(sortedData);
    } catch (err) {
      console.error('Failed to load patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadAllPatients();
      return;
    }

    setLoading(true);
    try {
      const response = await patientAPI.search(searchQuery);
      const data = response.data.data || response.data;
      // Sort by ID descending (newest first)
      const sortedData = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
      setPatients(sortedData);
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setMedicalHistory(extractMedicalProfile(patient));
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await visitAPI.create({
        patientId: patient.id,
        visitType: 'opd'
      });
      const data = response.data.data || response.data;
      setVisitId(data.visitId);
      setVisitInfo(data);
      setShowVitalsForm(true);
      if (data.billId) setAutoBill({ billId: data.billId, billNumber: data.billNumber });
    } catch (err) {
      setError('Failed to create visit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVitalsChange = (e) => {
    const { name, value } = e.target;
    setVitals({ ...vitals, [name]: value });

    if (name === 'weight' || name === 'height') {
      const weight = name === 'weight' ? parseFloat(value) : parseFloat(vitals.weight);
      const height = name === 'height' ? parseFloat(value) : parseFloat(vitals.height);

      if (weight && height) {
        const bmi = (weight / ((height / 100) ** 2)).toFixed(2);
        setVitals(prev => ({ ...prev, bmi }));
      }
    }
  };

  const handleMedicalHistoryChange = (e) => {
    setMedicalHistory({
      ...medicalHistory,
      [e.target.name]: e.target.value
    });
  };

  const handlePrint = useReactToPrint({
    contentRef: slipRef,
    documentTitle: 'OPD Slip',
    pageStyle: '@page { size: A4 portrait; margin: 6mm; }'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await visitAPI.recordVitals(visitId, vitals);
      await visitAPI.recordMedicalHistory(visitId, medicalHistory);

      // Persist medical profile back to patient record so it pre-fills next time
      await patientAPI.update(selectedPatient.id, {
        chiefComplaint:     medicalHistory.chiefComplaint,
        pastMedicalHistory: medicalHistory.pastMedicalHistory,
        currentMedications: medicalHistory.currentMedications,
        allergies:          medicalHistory.allergies,
      }).catch(() => {}); // non-blocking

      setSubmittedVitals({ ...vitals });
      const billMsg = autoBill ? ` Invoice ${autoBill.billNumber} generated for consultation fee.` : ' Consultation fee waived (visited within 30 days).';
      setSuccess(`Patient registered for OPD successfully! Sent to doctor for consultation.${billMsg}`);
      setSlipDialog(true);
    } catch (err) {
      setError('Failed to record patient information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSlipClose = () => {
    setSlipDialog(false);
    if (onSuccess) {
      onSuccess();
    } else {
      setSelectedPatient(null);
      setShowVitalsForm(false);
      setVisitId(null);
      setSubmittedVitals(null);
      setVitals({ bloodPressure: '', temperature: '', pulseRate: '', respiratoryRate: '',
        weight: '', height: '', bmi: '', oxygenSaturation: '', painScale: '' });
      setMedicalHistory({ chiefComplaint: '', pastMedicalHistory: '', currentMedications: '',
        allergies: '', familyHistory: '', lifestyleHabits: '' });
      setSearchQuery('');
      setPatients([]);
      setSuccess('');
    }
  };

  const content = (
    <>
      {!preselectedPatient && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonAdd sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            OPD Registration
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {loading && preselectedPatient && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Creating visit for patient...</Typography>
        </Box>
      )}

      {!showVitalsForm && !preselectedPatient && !loading ? (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by patient code, name, or phone"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Search />}
                sx={{ minWidth: 120 }}
              >
                Search
              </Button>
            </Box>

            {patients.length > 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>UHID</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Phone</strong></TableCell>
                      <TableCell><strong>Gender</strong></TableCell>
                      <TableCell align="center"><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patients.map((patient) => (
                      <TableRow key={patient.id} hover>
                        <TableCell>{patient.patientCode}</TableCell>
                        <TableCell>{patient.firstName} {patient.lastName}</TableCell>
                        <TableCell>{patient.phone}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{patient.gender}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSelectPatient(patient)}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}

        {showVitalsForm && selectedPatient && (
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.main' }}>
              <Typography variant="h6" color="primary">
                Patient: {selectedPatient.firstName} {selectedPatient.lastName}
              </Typography>
              {visitInfo && (
                <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>UHID:</strong> {selectedPatient.patientCode}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>OPD ID:</strong> {visitInfo.opdId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Yearly ID:</strong> {visitInfo.yearlyId}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Favorite sx={{ color: 'error.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">Vitals</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Blood Pressure"
                    name="bloodPressure"
                    value={vitals.bloodPressure}
                    onChange={handleVitalsChange}
                    placeholder="120/80"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Temperature (°F)"
                    name="temperature"
                    value={vitals.temperature}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pulse Rate (bpm)"
                    name="pulseRate"
                    value={vitals.pulseRate}
                    onChange={handleVitalsChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Respiratory Rate"
                    name="respiratoryRate"
                    value={vitals.respiratoryRate}
                    onChange={handleVitalsChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weight (kg)"
                    name="weight"
                    value={vitals.weight}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Height (cm)"
                    name="height"
                    value={vitals.height}
                    onChange={handleVitalsChange}
                    inputProps={{ step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="BMI"
                    name="bmi"
                    value={vitals.bmi}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: 'grey.100' }}
                    inputProps={{ step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Oxygen Saturation (%)"
                    name="oxygenSaturation"
                    value={vitals.oxygenSaturation}
                    onChange={handleVitalsChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ px: 2, pt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Pain Scale (0-10 Numerical Rating Scale)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Slider
                        value={vitals.painScale ? parseInt(vitals.painScale) : 0}
                        onChange={(_, value) => setVitals({ ...vitals, painScale: value })}
                        min={0}
                        max={10}
                        step={1}
                        marks
                        valueLabelDisplay="on"
                        sx={{ flexGrow: 1 }}
                      />
                      <TextField
                        type="number"
                        name="painScale"
                        value={vitals.painScale}
                        onChange={handleVitalsChange}
                        inputProps={{ min: "0", max: "10", step: "1" }}
                        sx={{ width: 80 }}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">😊 0 - No Pain</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {vitals.painScale !== '' && vitals.painScale !== null && (
                          <>
                            {parseInt(vitals.painScale) === 0 && '😊 No Pain'}
                            {parseInt(vitals.painScale) >= 1 && parseInt(vitals.painScale) <= 3 && '😐 Mild Pain'}
                            {parseInt(vitals.painScale) >= 4 && parseInt(vitals.painScale) <= 6 && '😣 Moderate Pain'}
                            {parseInt(vitals.painScale) >= 7 && parseInt(vitals.painScale) <= 9 && '😖 Severe Pain'}
                            {parseInt(vitals.painScale) === 10 && '😱 Worst Pain'}
                          </>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">😱 10 - Worst Pain</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospital sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">Medical History</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Chief Complaint"
                    name="chiefComplaint"
                    value={medicalHistory.chiefComplaint}
                    onChange={handleMedicalHistoryChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Past Medical History"
                    name="pastMedicalHistory"
                    value={medicalHistory.pastMedicalHistory}
                    onChange={handleMedicalHistoryChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Current Medications"
                    name="currentMedications"
                    value={medicalHistory.currentMedications}
                    onChange={handleMedicalHistoryChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Allergies"
                    name="allergies"
                    value={medicalHistory.allergies}
                    onChange={handleMedicalHistoryChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Family History"
                    name="familyHistory"
                    value={medicalHistory.familyHistory}
                    onChange={handleMedicalHistoryChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Lifestyle Habits"
                    name="lifestyleHabits"
                    value={medicalHistory.lifestyleHabits}
                    onChange={handleMedicalHistoryChange}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                fullWidth
              >
                {loading ? 'Submitting...' : 'Send to Doctor'}
              </Button>
              <Button
                type="button"
                variant="outlined"
                size="large"
                startIcon={<Cancel />}
                onClick={() => {
                  setShowVitalsForm(false);
                  setSelectedPatient(null);
                  setVisitId(null);
                }}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        )}
    </>
  );

  const slipDialogNode = (
    <Dialog open={slipDialog} onClose={handleSlipClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        OPD Registration Successful
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>
          Print OPD Slip
        </Button>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
        <div ref={slipRef}>
          <OPDSlipPrint
            patient={selectedPatient}
            visitInfo={visitInfo}
            vitals={submittedVitals}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSlipClose} color="primary" variant="outlined">
          Close & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );

  // If used in dialog (with preselectedPatient), return content without Card wrapper
  if (preselectedPatient) {
    return <>{content}{slipDialogNode}</>;
  }

  // If standalone mode, wrap in Card
  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        {content}
      </CardContent>
      {slipDialogNode}
    </Card>
  );
}

export default OPDRegistration;
